import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { AsYouType, CountryCode, getCountryCallingCode } from 'libphonenumber-js/min';
import { MaskitoOptions, MaskitoPostprocessor, MaskitoPreprocessor } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { PhoneCountryCodeService } from '../phone-country-code.service';

@Component({
  selector: 'app-phone-country-code-number-input',
  templateUrl: './phone-country-code-number-input.component.html',
  styleUrls: ['./phone-country-code-number-input.component.scss'],
  standalone: true,
  imports: [AsyncPipe, MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneCountryCodeNumberInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class PhoneCountryCodeNumberInputComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() countryCode: CountryCode = 'CA';
  @Input() error: boolean = false;
  @Input() controlId: string = '';
  @ViewChild('phoneInput', { static: true }) phoneInput!: ElementRef<HTMLInputElement>;

  private phoneCountryCodeService = inject(PhoneCountryCodeService);

  public displayValue: string = '';
  private rawValue: string = '';

  public maskOptions!: MaskitoOptions;

  public isDisabled = false;

  public interactionState$ = this.phoneCountryCodeService.interactionState$;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  public ngOnInit() {
    this.initializeMaskOptions();
    this.updateDisplayValue();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.countryCode) {
      this.rawValue = '';
      this.updateDisplayValue();
      this.onChange(this.rawValue);
      if (!changes.countryCode.isFirstChange()) {
        this.phoneInput.nativeElement.focus();
      }
    }
  }

  @HostListener('mouseenter')
  public onHover() {
    this.phoneCountryCodeService.setIsHovered(true);
  }

  @HostListener('mouseleave')
  public onLeave() {
    this.phoneCountryCodeService.setIsHovered(false);
  }

  public writeValue(value: string): void {
    this.rawValue = value || '';
    this.updateDisplayValue();
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  public handleInput(event: Event) {
    const input = event.target as HTMLInputElement;

    const countryCallingCode = getCountryCallingCode(this.countryCode);
    const fullNumeric = input.value.replace(/\D/g, '');

    let phoneNumber = fullNumeric;
    if (fullNumeric.startsWith(countryCallingCode)) {
      phoneNumber = fullNumeric.substring(countryCallingCode.length);
    }
    this.rawValue = phoneNumber;
    this.updateDisplayValue();
    this.onChange(this.rawValue);
  }

  public onFocus() {
    this.phoneCountryCodeService.setIsFocused(true);
  }

  public onBlur(): void {
    this.phoneCountryCodeService.setIsFocused(false);
    this.onTouched();
  }

  private updateDisplayValue(): void {
    if (!this.countryCode) return;

    const countryCallingCode = getCountryCallingCode(this.countryCode);
    if (!this.rawValue) {
      this.displayValue = `+${countryCallingCode} `;
      return;
    }
    // Scrubbing raw value of country code and adding in completeNumber
    const sanitizedRawValue = this.rawValue.replace(new RegExp(`^\\+?${countryCallingCode}`), '').replace(/\D/g, '');
    const completeNumber = `+${countryCallingCode}${sanitizedRawValue}`;
    const formatter = new AsYouType(this.countryCode);
    this.displayValue = formatter.input(completeNumber);
  }

  private initializeMaskOptions(): void {
    this.maskOptions = {
      mask: Array(15).fill(/\d/),
      preprocessors: [this.createPreprocessor()],
      postprocessors: [this.createPostprocessor()]
    };
  }

  private createPreprocessor(): MaskitoPreprocessor {
    return ({ elementState, data }, actionType) => {
      const { value, selection } = elementState;
      const countryCallingCode = getCountryCallingCode(this.countryCode);
      const prefix = `+${countryCallingCode}`;

      // 1. Extract raw digits from the formatted input value.
      // This is the string Maskito will operate on internally.
      let processedValue = '';
      if (value.startsWith(prefix)) {
        processedValue = value.substring(prefix.length).replace(/\D/g, '');
      } else {
        processedValue = value.replace(/\D/g, '');
      }

      // 2. Calculate new selection (cursor position) in the processed raw string.
      const [from, to] = selection;

      const unformatCursorPosition = (
        currentFormattedValue: string,
        formattedCursorPosition: number,
        actualPrefix: string
      ): number => {
        let rawPos = 0;
        if (formattedCursorPosition <= actualPrefix.length) {
          return 0;
        }

        const charsToConsider = currentFormattedValue.substring(actualPrefix.length, formattedCursorPosition);
        for (let i = 0; i < charsToConsider.length; i++) {
          if (/\d/.test(charsToConsider[i])) {
            rawPos++;
          }
        }
        return rawPos;
      };

      const newFrom = unformatCursorPosition(value, from, prefix);
      const newTo = unformatCursorPosition(value, to, prefix);

      this.rawValue = processedValue; // Update the component's internal rawValue

      return {
        elementState: {
          value: processedValue,
          selection: [newFrom, newTo]
        },
        data
      };
    };
  }
  private createPostprocessor(): MaskitoPostprocessor {
    return (elementState, initialElementState) => {
      // Correct signature
      const { value, selection } = elementState; // Destructure value and selection from the provided elementState
      const [from, to] = selection;
      const countryCallingCode = getCountryCallingCode(this.countryCode);
      const prefix = `+${countryCallingCode}`;

      // `value` here is the raw numeric string (from Maskito's internal processing).
      // Apply AsYouType formatting to this rawValue.
      const formatter = new AsYouType(this.countryCode);
      // Ensure AsYouType formats the full international number by prepending the prefix
      const formattedValue = formatter.input(`${prefix}${value}`);

      // Calculate new selection (cursor position) in the formatted string.
      const formatCursorPosition = (formattedStr: string, rawPosition: number, actualPrefix: string): number => {
        const formattedPos = actualPrefix.length; // Start searching after the prefix
        let digitCount = 0;

        // If raw position is 0, the cursor should be right after the prefix.
        if (rawPosition === 0) {
          return formattedPos;
        }

        // Iterate through the formatted string (starting after the prefix)
        // to find the character index corresponding to the rawPosition.
        for (let i = formattedPos; i < formattedStr.length; i++) {
          if (/\d/.test(formattedStr[i])) {
            digitCount++; // Increment digit count if it's a digit
            if (digitCount === rawPosition) {
              // If we've found the raw digit corresponding to rawPosition
              return i + 1; // Cursor should be *after* this digit
            }
          }
        }
        // If we reach here, it means rawPosition is at or beyond the last digit.
        // In this case, the cursor should be at the very end of the formatted string.
        return formattedStr.length;
      };

      const newFrom = formatCursorPosition(formattedValue, from, prefix);
      const newTo = formatCursorPosition(formattedValue, to, prefix);

      // If the raw value was empty, ensure the display shows only the prefix
      // and the cursor is placed correctly.
      if (!value && from === 0 && to === 0) {
        // Check `value` (raw digits)
        this.displayValue = prefix;
        this.onChange(''); // Notify parent about empty raw value
        return {
          // Return ElementState object
          value: prefix,
          selection: [prefix.length, prefix.length]
        };
      }

      this.displayValue = formattedValue; // Update display value for the input
      this.onChange(this.rawValue); // Notify parent about the raw (unformatted) value

      return {
        // Return ElementState object
        value: formattedValue,
        selection: [newFrom, newTo]
      };
    };
  }
}
